import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { IonPage, useIonRouter, useIonViewDidEnter, useIonViewWillEnter } from '@ionic/react';
import { useDispatch, useSelector } from 'react-redux';
import { favouriteSpecialistsListSelector } from 'src/redux/favouriteSpecialits/selector';
import {
  AppHeaderPageFeature,
  Container_v2 as Container,
  SpecialistPreviewListWidget,
  EmptyList,
  Specialist,
} from '@mindly/ui-components';
import { useSwipeBackGesture } from 'src/shared/hooks/useSwipeBackGesture';
import { useFavouriteSpecialist } from 'src/hooks/useFavouriteSpecialist';
import { useTranslation } from 'react-i18next';
import { usersPsychologistsSelector } from 'src/redux/usersPsychologists/selector';
import { getUsersPsychologistsListThunk } from 'src/redux/usersPsychologists/action';
import useToast from 'src/context/ToastContext/ToastContext';
import { ImpactStyle } from '@capacitor/haptics';
import { PrivateRoutes } from 'src/routes/navigation';
import { pageAnimation } from 'src/shared';
import { isomorficHapticsImpact } from 'src/shared/utils/haptics';
import { userSelector } from 'src/redux/user/selector';
import { PublicRoutes } from 'src/routes/navigation';
import { useAnalytics } from 'src/shared';
import { favouriteSpecialistsLoadingSelector } from 'src/redux/favouriteSpecialits/selector';
import { fetchFavouriteSpecialists } from 'src/redux/favouriteSpecialits/thunk';
import { useHistory } from 'react-router';
import { setIsActive } from 'src/redux/settingsModal/reducer';

const FavouriteSpecialists: React.FC = () => {
  const favouriteSpecialistList = useSelector(favouriteSpecialistsListSelector);
  const usersPsychologistList = useSelector(usersPsychologistsSelector);
  const { isFavouriteSpecialist, toggleFavourite } = useFavouriteSpecialist();
  const navigation = useIonRouter();
  const pageRef = useRef<HTMLDivElement>(null);
  useSwipeBackGesture(pageRef);
  const [itemsFavourite, setItemsFavourite] = useState<Specialist[]>(
    favouriteSpecialistList ? (favouriteSpecialistList as unknown as Specialist[]) : []
  );
  const [itemsHasSessions, setItemsHasSessions] = useState<Specialist[]>(
    usersPsychologistList ? (usersPsychologistList as unknown as Specialist[]) : []
  );
  const { t } = useTranslation('ui');
  const dispatch = useDispatch();
  const { showToast } = useToast();
  const [isUsersPsychologistsLoading, setIsUsersPsychologistsLoading] = useState(usersPsychologistList === null);

  const history = useHistory();
  const user = useSelector(userSelector);
  const { trackPredefinedEvent } = useAnalytics();

  const favouriteSpecialistsListStatus = useSelector(favouriteSpecialistsLoadingSelector);

  const [isLoadingFavouriteSpecialists, setIsLoadingFavouriteSpecialists] = useState(false);

  useIonViewDidEnter(() => {
    trackPredefinedEvent('FAVOURITES_SCREEN_VIEWED');
  }, [trackPredefinedEvent]);

  useIonViewWillEnter(() => {
    if (!user?.id) {
      history.push(PublicRoutes.AUTH);
      return;
    }
    dispatch(setIsActive(false));
  }, [user?.id]);

  useIonViewWillEnter(async () => {
    try {
      if (favouriteSpecialistsListStatus === 'succeeded' || !user) return;

      setIsLoadingFavouriteSpecialists(true);
      await dispatch(fetchFavouriteSpecialists());
    } catch (error) {
      console.error(error);
      showToast({ error });
    } finally {
      setIsLoadingFavouriteSpecialists(false);
    }
  }, [favouriteSpecialistsListStatus, user]);

  const handleClose = useCallback(() => {
    navigation.goBack((baseEl, opts) => pageAnimation(baseEl, opts, true));
  }, [navigation]);

  const handleToggleFavourite = useCallback(
    (specialist: Specialist) => {
      toggleFavourite(specialist, (action) => {
        showToast({ text: t(`selected_specialists.toggle_favourite.${action}`) });
      });
    },
    [toggleFavourite]
  );

  useEffect(() => {
    if (itemsFavourite.length) return;
    if (favouriteSpecialistList !== null) {
      setItemsFavourite(favouriteSpecialistList as unknown as Specialist[]);
    }
  }, [itemsFavourite.length, favouriteSpecialistList]);

  useEffect(() => {
    if (itemsHasSessions.length) return;
    if (usersPsychologistList !== null) {
      return setItemsHasSessions(usersPsychologistList as unknown as Specialist[]);
    }
    (async () => {
      try {
        setIsUsersPsychologistsLoading(true);
        await dispatch(getUsersPsychologistsListThunk());
      } finally {
        setIsUsersPsychologistsLoading(false);
      }
    })();
  }, [itemsHasSessions, usersPsychologistList]);

  const isLoading = useMemo(
    () => isLoadingFavouriteSpecialists || isUsersPsychologistsLoading,
    [isLoadingFavouriteSpecialists, isUsersPsychologistsLoading]
  );

  const onCardClickHandler = useCallback(async (specialist: Specialist) => {
    isomorficHapticsImpact({ style: ImpactStyle.Light });
    navigation.push(PrivateRoutes.PSYCHOLOGIST_PROFILE_TAB.replace(':id', specialist.id));
  }, []);

  return (
    <IonPage ref={pageRef}>
      <AppHeaderPageFeature onClose={handleClose}>{t('selected_specialists.title')}</AppHeaderPageFeature>
      <Container>
        {!itemsFavourite?.length && !itemsHasSessions?.length && !isLoading ? (
          <div className="flex justify-center items-center h-full">
            <EmptyList t={t} />
          </div>
        ) : (
          <>
            {(!!itemsHasSessions?.length || isUsersPsychologistsLoading) && (
              <SpecialistPreviewListWidget
                title={t('selected_specialists.has_sessions.title')}
                specialists={
                  (itemsHasSessions?.map((it) => ({
                    ...it,
                    isFavourite: isFavouriteSpecialist(it.id),
                  })) as Specialist[]) ?? []
                }
                isLoading={isUsersPsychologistsLoading}
                onToggleFavourite={handleToggleFavourite}
                t={t}
                onCardClick={onCardClickHandler}
              />
            )}
            {(!!itemsFavourite?.length || isLoadingFavouriteSpecialists) && (
              <SpecialistPreviewListWidget
                title={t('selected_specialists.favourite.title')}
                specialists={
                  (itemsFavourite?.map((it) => ({
                    ...it,
                    isFavourite: isFavouriteSpecialist(it.id),
                  })) as Specialist[]) ?? []
                }
                isLoading={isLoadingFavouriteSpecialists}
                onToggleFavourite={handleToggleFavourite}
                onCardClick={onCardClickHandler}
                t={t}
              />
            )}
          </>
        )}
      </Container>
    </IonPage>
  );
};

export default FavouriteSpecialists;
